import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** Routes where a surprise reload would destroy in-progress work. */
function isProtectedRoute(pathname: string): boolean {
  // `/room/*` is an active game; `/build` holds an unsaved code editor.
  return pathname.startsWith('/room/') || pathname.startsWith('/build');
}

/**
 * Silently reloads the page when a new service worker takes control.
 * Works on all platforms including iOS PWA (standalone mode).
 *
 * - First-time installs are skipped (no previous controller → no reload)
 * - Subsequent updates reload automatically without any user prompt
 * - Reloads are deferred while the user is somewhere they'd lose work
 */
export function UpdatePrompt() {
  // Register the service worker via vite-plugin-pwa
  useRegisterSW();

  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Capture whether a SW was already in control before we attach the listener
    let hadController = Boolean(navigator.serviceWorker.controller);

    const handler = () => {
      if (hadController) {
        // A new SW just took control — reload to pick up fresh cached assets.
        if (isProtectedRoute(pathnameRef.current)) {
          sessionStorage.setItem('pendingUpdate', '1');
        } else {
          window.location.reload();
        }
      }
      hadController = Boolean(navigator.serviceWorker.controller);
    };

    navigator.serviceWorker.addEventListener('controllerchange', handler);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handler);
  }, []);

  // Flush a deferred update once the user leaves the protected route.
  // This must key off the router's location: `popstate` does not fire for the
  // pushState navigations that <Link> and navigate() perform, so an update
  // deferred in a room used to stay stuck until a browser back/forward.
  useEffect(() => {
    if (isProtectedRoute(location.pathname)) return;
    if (sessionStorage.getItem('pendingUpdate')) {
      sessionStorage.removeItem('pendingUpdate');
      window.location.reload();
    }
  }, [location.pathname]);

  return null;
}
